/**
 * THE ONE PLACE THE OPERATOR OF BOOK WITH ASSEMBLE IS DESCRIBED.
 *
 * Every published policy (src/shared/policies), the footer, and any
 * customer-facing legal sentence reads the operator's identity from
 * here and nowhere else. To record a registered business identity later,
 * fill the fields in below — one edit, and every document updates on
 * the next deploy, because the documents are BUILT from this object
 * rather than written around it.
 *
 * WHAT IS NOT INVENTED. No company name, entity form, registered DBA,
 * address, tax or registration number is written here or anywhere else
 * in this repository. While `legalOperatorDisplayName` is null the
 * documents use the neutral wording "the operator of the Book with
 * Assemble service", which is true, binds the party actually providing
 * the service, and claims no legal person that does not exist. A blank,
 * a placeholder, a "TODO" or a "this document is incomplete" notice is
 * never shown: `scripts/check-legal.mjs` refuses a value that looks like
 * one, and `tests/db/trust-safety.test.ts` proves the rendered text
 * carries none.
 *
 * "Book with Assemble" is the PRODUCT and service name only. It is not
 * the name of a company, and nothing here says it is.
 */

export interface LegalIdentity {
  /** The product and service name every document uses. */
  readonly legalProductName: string;
  /**
   * The registered legal name of the operator, once one exists, e.g.
   * "Example Holdings LLC". Null means no registered identity is
   * recorded and the neutral wording is used throughout.
   */
  readonly legalOperatorDisplayName: string | null;
  /**
   * The entity form, e.g. "a Delaware limited liability company". Read
   * only when a display name is set, and only when the display name does
   * not already carry it.
   */
  readonly legalOperatorEntityType: string | null;
  /** A postal address for legal notice, once one is published. */
  readonly legalBusinessAddress: string | null;
  /** The address policy, privacy and copyright questions are sent to. */
  readonly legalSupportEmail: string | null;
  /**
   * Governing law and venue, e.g. "the State of Florida, United States".
   * Null means the documents say the law of the place the operator is
   * established, which is the neutral and truthful default.
   */
  readonly legalGoverningLaw: string | null;
  /** ISO date (YYYY-MM-DD) the current documents took effect. */
  readonly legalEffectiveDate: string;
  /** Bumped whenever any document's text changes; consents record it. */
  readonly legalDocumentVersion: string;
}

export const LEGAL_IDENTITY: LegalIdentity = {
  legalProductName: 'Book with Assemble',
  legalOperatorDisplayName: null,
  legalOperatorEntityType: null,
  legalBusinessAddress: null,
  legalSupportEmail: null,
  legalGoverningLaw: null,
  legalEffectiveDate: '2026-09-20',
  legalDocumentVersion: '1.3.0',
};

function clean(value: string | null | undefined): string | null {
  const v = value?.trim();
  return v ? v : null;
}

/**
 * How the operator is referred to in running text, in the middle of a
 * sentence: "the operator of the Book with Assemble service", or the
 * registered name once one is recorded.
 */
export function operatorName(identity: LegalIdentity = LEGAL_IDENTITY): string {
  const name = clean(identity.legalOperatorDisplayName);
  if (!name) return `the operator of the ${identity.legalProductName} service`;
  const type = clean(identity.legalOperatorEntityType);
  if (type && !name.toLowerCase().includes(type.toLowerCase())) return `${name}, ${type}`;
  return name;
}

/** The same, for the start of a sentence. */
export function operatorNameCapitalised(identity: LegalIdentity = LEGAL_IDENTITY): string {
  const name = operatorName(identity);
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** True once a registered legal identity has been recorded. */
export function operatorIdentityRecorded(identity: LegalIdentity = LEGAL_IDENTITY): boolean {
  return clean(identity.legalOperatorDisplayName) !== null;
}

/**
 * The phrase the documents use for the law that governs them. Neutral
 * while no jurisdiction is recorded: it names no place, and it is true.
 */
export function governingLawPhrase(identity: LegalIdentity = LEGAL_IDENTITY): string {
  const law = clean(identity.legalGoverningLaw);
  if (law) return `the laws of ${law}`;
  return `the laws of the jurisdiction in which ${operatorName(identity)} is established`;
}

/**
 * The sentences that tell a reader how to reach the operator. Empty when
 * nothing is recorded: a section is then simply not rendered, rather than
 * rendered with a blank where an address should be.
 */
export function operatorContactLines(identity: LegalIdentity = LEGAL_IDENTITY): string[] {
  const lines: string[] = [];
  const email = clean(identity.legalSupportEmail);
  const address = clean(identity.legalBusinessAddress);
  if (email) lines.push(`Questions about this document and requests under it can be sent to ${email}.`);
  if (address) lines.push(`Legal notice may be sent by post to ${address}.`);
  return lines;
}

/**
 * The definitions every policy opens with. Written once so that the six
 * documents cannot drift from each other on what "we" and "you" mean.
 */
export function legalDefinitions(identity: LegalIdentity = LEGAL_IDENTITY): string[] {
  const product = identity.legalProductName;
  const operatorClause = operatorIdentityRecorded(identity)
    ? `mean ${operatorName(identity)}, the provider and operator of the ${product} software service.`
    : `mean the provider and operator of the ${product} software service.`;
  return [
    `“${product}” means the ${product} software service.`,
    `“Operator,” “we,” “us,” and “our” ${operatorClause}`,
    `“Business,” “you,” and “your” mean the person or business that creates an account, `
    + `purchases a subscription, or uses the ${product} service.`,
    `“Customer” means an end customer who books, purchases, rents, registers, or otherwise `
    + `transacts with a Business using ${product}.`,
  ];
}

/**
 * Words that must never appear in a published document. A blank field
 * is caught by the type; these catch the ways a blank gets papered over.
 */
export const FORBIDDEN_LEGAL_PHRASES: readonly RegExp[] = [
  /\bTODO\b/i,
  /\bTBD\b/i,
  /\[[^\]]*\]/,                       // "[COMPANY NAME]", "[address]"
  /\bplaceholder\b/i,
  /this (document|section|policy) is (incomplete|not in force)/i,
  /\bnot (yet )?in force\b/i,
  /\bunresolved\b/i,
  /has not been recorded/i,
  /\blorem ipsum\b/i,
];
